/**
 * @fileoverview Ads-framework storefront (commerce) instrumentation.
 * @description Type-safe, dual-platform event helpers for the a-la-carte
 * storefront conversion funnel — checkout and the framework zip download.
 *
 * Why a dedicated module (not the `~/lib/analytics` resource singleton):
 * the resource analytics layer is keyed on `ResourceType` (`calculator`,
 * `quiz`, …) and models content engagement. The storefront funnel is a
 * different domain — money-moving actions (begin checkout, purchase) and
 * commerce-specific surfaces (license download) — so it gets
 * its own cohesive, GA4-ecommerce-shaped vocabulary instead of being
 * shoehorned into a `resource_*` event.
 *
 * Design:
 * - Each event fans out to GA4 AND Vercel through one private `dispatch`.
 * - BOTH halves are consent-gated: GA4 via `sendEvent`'s internal check,
 *   Vercel via the same `isGaAnalyticsTrackingAllowed` signal. These
 *   commerce events carry a transaction/license id, so an analytics
 *   opt-out (or a GPC signal) is honored on both platforms — matching
 *   gtag's own GA→Vercel mirror, which only fans out after the consent
 *   gate, rather than leaking identifier-bearing events past an opt-out.
 * - Never throws — analytics must never break the purchase UX.
 * - Safe on the server (every primitive no-ops when `window` is absent).
 * - Zero extra runtime cost beyond a `track()` call: no timers, no
 *   listeners, no observers (callers fire these from existing handlers).
 *
 * GA4 ecommerce note: `begin_checkout` and `purchase` are GA4's
 * recommended ecommerce event names AND carry `value` + `currency`, so
 * they populate the standard Monetization (revenue) reports without custom
 * config. The launch-promo and download events are storefront-custom (no
 * GA4 recommended equivalent).
 */

import { track } from "@vercel/analytics"

import {
  getAttributionProperties,
  getAttributionPropertiesForGa4,
  recordAttributionTouchAsync,
  recordAttributionTouchAsyncBounded,
  CHECKOUT_ATTRIBUTION_BUDGET_MS,
} from "~/lib/analytics/attribution"
import { isGaAnalyticsTrackingAllowed, sendEvent } from "~/lib/gtag"
import { getConversionIntelligenceProps } from "~/lib/site-intelligence/session-engagement"
import { recordJourneyPage } from "~/lib/site-intelligence/session-journey"
// Single source of truth for the SKU union — the typed analytics registry.
// (This module previously kept a parallel copy; unified so the two can't drift.)
import type { EventPropValue, StorefrontProduct } from "~/lib/analytics/registry"

/** Flat property bag Vercel Analytics accepts (no nested objects/arrays). */
type VercelProps = Record<string, string | number | boolean>

const CURRENCY = "USD"

/**
 * Current pathname, best-effort, for segmenting the same action across the
 * surfaces it appears on (framework landing vs. skills page vs. /account).
 * Returns `undefined` on the server so the param is simply omitted.
 */
function currentPath(): string | undefined {
  if (typeof window === "undefined") return undefined
  return window.location.pathname
}

/**
 * Query-stripped page location (`origin` + `pathname`) for GA4's
 * `page_location` param. `sendEvent` otherwise defaults `page_location`
 * to the full `window.location.href` — which on `/checkout/success`
 * embeds the live Stripe `session_id` and would ship that credential to
 * GA4 (a third party). The success page deliberately won't even render
 * the license key for that reason, so the analytics layer must not leak
 * the session id either. Stripping the query (and hash) here applies the
 * same hardening to every commerce event, none of which need query args.
 */
function safePageLocation(): string | undefined {
  if (typeof window === "undefined") return undefined
  return window.location.origin + window.location.pathname
}

/**
 * Fan one logical storefront event out to GA4 and Vercel. Both sends are
 * best-effort (wrapped) and consent-gated; the Vercel send additionally
 * no-ops on the server. The GA action and Vercel event names are passed
 * separately because they diverge for `purchase` (GA4 `purchase` ↔ the
 * site-wide Vercel `conversion` event).
 */
function dispatch(
  ga: { action: string; params: Record<string, unknown> },
  vercel: { event: string; props: VercelProps },
  pageLocationOverride?: string,
): void {
  // GA4 — consent-gated internally by sendEvent. Inject a query-stripped
  // `page_location` (so the Stripe `session_id` never reaches GA4); a
  // caller-supplied value in ga.params still wins via spread order.
  const loc = pageLocationOverride ?? safePageLocation()
  try {
    sendEvent({
      action: ga.action,
      params: { ...(loc ? { page_location: loc } : {}), ...ga.params },
    })
  } catch {
    // best-effort
  }
  // Vercel — gate on the SAME consent signal so the two halves stay
  // symmetric and an analytics opt-out is honored on both platforms.
  if (typeof window === "undefined" || !isGaAnalyticsTrackingAllowed()) return
  try {
    track(vercel.event, vercel.props)
  } catch {
    // Analytics must never break UX.
  }
}

/**
 * Buyer landed on a storefront product page — the TOP of the commerce funnel,
 * fired once on mount so view→checkout drop-off is measurable (previously the
 * earliest commerce signal was `begin_checkout`). GA4 `view_item` (recommended
 * ecommerce, with `value`/`currency`/`items`) populates Monetization reports.
 */
export async function trackViewItem(options: {
  product: StorefrontProduct
  /** List price of the SKU in whole USD. */
  valueUsd: number
  /** Optional surface label, e.g. the SKU page id. */
  placement?: string
}): Promise<void> {
  const dims: VercelProps = { product: options.product, value: options.valueUsd }
  if (options.placement) dims.placement = options.placement
  const path = currentPath()
  if (path) dims.surface_path = path
  const pageLocation = safePageLocation()
  // `view_item` is a one-shot fired from the page's OWN mount effect
  // (ProductViewAnalytics), which can race AHEAD of the dynamically-imported
  // layout `Analytics` effect that normally captures the touch. Hydrate region
  // before the synchronous EEA gate so first UK product landings do not persist
  // attribution pre-opt-in.
  if (path) await recordAttributionTouchAsyncBounded(path)
  // Acquisition source + the on-site page that referred this product view, so
  // the commerce funnel is attributable to where the buyer came from end to end.
  // GA4 gets the curated subset (25-param cap); Vercel keeps the full bag.
  const ga4Attribution = getAttributionPropertiesForGa4()
  const fullAttribution = getAttributionProperties()

  dispatch(
    {
      action: "view_item",
      params: {
        currency: CURRENCY,
        value: options.valueUsd,
        // Complete GA4 ecommerce item: `item_id` is the SKU slug, which is
        // also the Merchant Center feed's `g:id` (see `~/lib/products/catalog`),
        // so GA's product-level metrics line up with the Shopping listing.
        items: [{ item_id: options.product, price: options.valueUsd, quantity: 1 }],
        ...ga4Attribution,
        ...dims,
      },
    },
    { event: "view_item", props: { ...fullAttribution, ...dims } },
    pageLocation,
  )
}

/**
 * Buyer clicked a checkout CTA — fires before the `/api/checkout` handoff
 * so the funnel's top is measured even when the Stripe redirect (or the
 * POST) fails. GA4 `begin_checkout` (with `value`/`currency`) so it
 * populates Monetization reports.
 */
export async function trackBeginCheckout(options: {
  product: StorefrontProduct
  /** Where the CTA lives, e.g. `"hero"`, `"pricing"`, `"final_cta"`. */
  placement?: string
  /**
   * List price of the SKU in whole USD, for the GA4/Vercel `value` param.
   * Intent-stage value: it's the list price, not any promo-discounted
   * total (the code is applied later, at Stripe). Omitting it leaves the
   * dimension unset.
   */
  valueUsd?: number
}): Promise<void> {
  const dims: VercelProps = { product: options.product }
  if (options.placement) dims.placement = options.placement
  const path = currentPath()
  if (path) dims.surface_path = path
  if (typeof options.valueUsd === "number") dims.value = options.valueUsd
  // Capture the landing/campaign touch BEFORE dispatch so first-click checkout
  // CTAs (no prior ProductViewAnalytics) still carry source/campaign on the event.
  if (path) {
    await recordAttributionTouchAsyncBounded(path)
  }
  const ga4Attribution = getAttributionPropertiesForGa4()
  const fullAttribution = getAttributionProperties()

  // Complete GA4 ecommerce item; `price` is only known when a list price was
  // supplied (it's an optional intent-stage value — see `valueUsd` above).
  const item: Record<string, EventPropValue> = { item_id: options.product, quantity: 1 }
  if (typeof options.valueUsd === "number") item.price = options.valueUsd

  dispatch(
    {
      action: "begin_checkout",
      params: { currency: CURRENCY, items: [item], ...ga4Attribution, ...dims },
    },
    { event: "begin_checkout", props: { ...fullAttribution, ...dims } },
  )
}

/**
 * The `/api/checkout` handoff failed (non-OK response or network error) —
 * the buyer sees the recovery banner. Tracking this gives the checkout
 * drop-off a denominator: how many begin_checkouts never reached Stripe.
 */
export function trackCheckoutError(options: {
  product: StorefrontProduct
  /** HTTP status when the failure was a non-OK response; omit for a throw. */
  status?: number
  placement?: string
}): void {
  const dims: VercelProps = { product: options.product }
  if (typeof options.status === "number") dims.status = options.status
  if (options.placement) dims.placement = options.placement
  const path = currentPath()
  if (path) dims.surface_path = path

  dispatch(
    { action: "checkout_error", params: { ...dims } },
    { event: "checkout_error", props: dims },
  )
}

/**
 * Purchase confirmed — fired client-side from the post-purchase landing
 * once the license row is resolved. Split into two platform-specific
 * halves because they have different readiness constraints:
 *
 * - **Vercel** (`trackPurchaseVercel`) has no `gtag` dependency, so the
 *   beacon fires it immediately on mount — it must never be delayed
 *   behind, or dropped by, the GA readiness wait.
 * - **GA4** (`trackPurchaseGa`) needs `window.gtag`, which loads
 *   `lazyOnload`, so the beacon waits for `isGtagReady()` before firing
 *   it (see `PurchaseConfirmedBeacon`).
 *
 * `transactionId` is the license ROW id (the value already in the
 * auth-gated `/account/licenses/[id]` URL), NOT the secret license key. It
 * is sent as the GA4 `transaction_id` / Vercel order identifier. It does
 * NOT, by itself, dedupe revisits: neither Vercel nor GA4 standard reports
 * apply transaction-keyed idempotency client-side. The caller
 * (`PurchaseConfirmedBeacon`) is responsible for firing each half at most
 * once per license via persisted (localStorage) markers.
 */
interface PurchaseOptions {
  product: StorefrontProduct
  transactionId: string
  /**
   * Human-readable product title (the catalog `title`, e.g. "Adsights Ads
   * Framework") for the GA4 ecommerce `item_name`. Resolved server-side from
   * `~/lib/products/catalog` so it can't drift from the Merchant Center feed
   * and adds no client-bundle weight. Only the GA4 half uses it.
   */
  productName: string
  /** Actual amount paid, in whole currency units (i.e. cents ÷ 100). */
  valueUsd: number
  /** ISO-4217 currency code (caller uppercases the stored value). */
  currency: string
}

/**
 * GA4 `purchase` half. Consent-gated inside `sendEvent`; ships a
 * query-stripped `page_location` so the Stripe `session_id` on the
 * success URL never reaches GA4.
 */
export async function trackPurchaseGa(options: PurchaseOptions): Promise<void> {
  const loc = safePageLocation()
  // Record the success route's touch BEFORE reading attribution, so a purchase
  // beacon that fires before the layout Analytics effect advances the nav pair
  // still reports the SKU/checkout page as `referrer_path` (same one-shot
  // mount-order race handled in trackViewItem). Idempotent + consent/region-gated.
  const path = currentPath()
  if (path) {
    await recordAttributionTouchAsyncBounded(path, CHECKOUT_ATTRIBUTION_BUDGET_MS)
    recordJourneyPage(path)
  }
  // First/last touch persists across the Stripe round-trip (localStorage), so a
  // purchase attributes back to the campaign/referrer that originally acquired
  // the buyer — not the Stripe success-page referrer. GA4 gets the curated
  // subset (the full ecommerce item + revenue dims already spend params; the
  // full attribution bag risks the 25-param cap).
  const attribution = getAttributionPropertiesForGa4()
  const intelligence = getConversionIntelligenceProps(path ?? "/")
  try {
    sendEvent({
      action: "purchase",
      params: {
        ...(loc ? { page_location: loc } : {}),
        ...attribution,
        ...intelligence,
        currency: options.currency,
        value: options.valueUsd,
        transaction_id: options.transactionId,
        // Complete GA4 ecommerce item. `item_id` is the SKU slug — identical
        // to the Merchant Center feed's `g:id` — so once GA4 is linked as a
        // Merchant Center key-event source and `purchase` is flagged a Key
        // Event, the purchase attributes to the matching Shopping product.
        //
        // `price` is the ACTUAL per-unit amount paid (order is a single unit,
        // quantity 1), so it equals the order `value`. This is intentional:
        // GA4 ecommerce reports the revenue actually earned, so a promo-code
        // purchase reports below the feed's list `g:price` — the `item_id`
        // match (not price equality) is what links the event to the listing.
        // CAVEAT: `valueUsd` derives from Stripe `amount_total`, which INCLUDES
        // tax when `STRIPE_AUTOMATIC_TAX_ENABLED=true` (off by default). When an
        // operator enables Stripe Tax, both `value` and item `price` would
        // fold tax into SKU revenue; splitting tax out needs the Stripe
        // subtotal/tax breakdown persisted on the license row (a webhook +
        // schema change, tracked separately).
        items: [
          {
            item_id: options.product,
            item_name: options.productName,
            price: options.valueUsd,
            quantity: 1,
          },
        ],
        product: options.product,
      },
    })
  } catch {
    // best-effort
  }
}

/**
 * Vercel `conversion` half. Consent-gated on the same
 * `isGaAnalyticsTrackingAllowed` signal as the GA path. No `gtag`
 * dependency — queues immediately with whatever attribution is already
 * stored (first touch survives the Stripe round-trip). Does not await
 * region hydration so a fast tab-close never drops the conversion.
 */
export function trackPurchaseVercel(options: PurchaseOptions): Promise<void> {
  // Deliberately NOT `async`: unlike the GA half (`trackPurchaseGa`, which
  // awaits region hydration), this path queues immediately and never awaits, so
  // a fast tab-close can't drop the conversion. The `Promise<void>` return type
  // is kept for API symmetry with `trackPurchaseGa` (callers `void` or `await`
  // either half interchangeably), so it returns a resolved promise rather than
  // being marked `async` with no `await` expression.
  //
  // The whole body is wrapped so an unexpected throw ANYWHERE surfaces as a
  // REJECTED promise — the channel an `async` body would use — never as a
  // synchronous throw. The production caller (`PurchaseConfirmedBeacon`) hangs
  // `.catch()/.finally()` off this promise for retry + in-flight cleanup; a
  // sync throw would skip both (leaking the in-flight guard) and propagate
  // into React's commit phase on the post-purchase page.
  try {
    if (typeof window === "undefined" || !isGaAnalyticsTrackingAllowed()) return Promise.resolve()
    const path = currentPath()
    if (path) {
      void recordAttributionTouchAsync(path).catch(() => {
        // Best-effort nav-pair advance: an ASYNC (post-invocation) failure is
        // absorbed here so it can't block the conversion queue. A SYNC throw
        // at the invocation itself instead rejects the whole call via the
        // outer catch — faithful to the pre-sweep `async` body, where such a
        // throw also rejected the returned promise (#622 review).
      })
      recordJourneyPage(path)
    }
    const attribution = getAttributionProperties()
    const intelligence = getConversionIntelligenceProps(path ?? "/")
    try {
      track("conversion", {
        conversion_type: "purchase",
        product: options.product,
        transaction_id: options.transactionId,
        value: options.valueUsd,
        currency: options.currency,
        ...attribution,
        ...intelligence,
      })
    } catch {
      // Analytics must never break UX. A failed `track` send is absorbed (the
      // promise still resolves), matching the GA half's absorbed `sendEvent`.
    }
    return Promise.resolve()
  } catch (error) {
    return Promise.reject(error instanceof Error ? error : new Error(String(error)))
  }
}

/**
 * What the buyer did on the license zip-download surface. These all fire
 * on click (intent), NOT on a confirmed download response — the server
 * only stamps `zip_first_downloaded_at` after a successful authenticated
 * GET stream. Hence `first_download_intent` (not `first_download`): a 401
 * (expired session), 503 (missing zip), or aborted transfer would still
 * emit the click event while the server-side refund-policy lock never
 * flips, so the name must read as intent, not the irreversible action.
 */
type DownloadAction =
  | "gate_open" // opened the refund-waiver confirmation gate
  | "gate_cancel" // backed out of the gate to the npm path
  | "first_download_intent" // clicked confirm on the gated first download
  | "re_download" // clicked re-download on an already-downloaded license

/**
 * Zip-download surface on the license detail page. The first download is
 * the irreversible, refund-affecting action, so each step of the gate is
 * worth measuring (open → confirm vs. open → cancel) alongside the plain
 * re-download path. Carries `product` so the framework and skills-only
 * download funnels are segmentable without joining the license id back to
 * the database.
 */
export function trackFrameworkDownload(options: {
  action: DownloadAction
  product: StorefrontProduct
  licenseId: string
}): void {
  const props: VercelProps = {
    download_action: options.action,
    product: options.product,
    license_id: options.licenseId,
  }
  dispatch(
    { action: "framework_download", params: { ...props } },
    { event: "framework_download", props },
  )
}
